function getSharedTitlePrefix(titles) {
  const wordSets = titles
    .map((title) => String(title || '').trim().split(/\s+/).filter(Boolean))
    .filter((words) => words.length);

  if (wordSets.length < 2) return '';

  let sharedWordCount = Math.min(...wordSets.map((words) => words.length));
  for (let index = 0; index < sharedWordCount; index += 1) {
    if (wordSets.some((words) => words[index].toLowerCase() !== wordSets[0][index].toLowerCase())) {
      sharedWordCount = index;
      break;
    }
  }

  const hasDifferentiator = wordSets.every((words) => words.length > sharedWordCount);
  if (sharedWordCount < 2 || !hasDifferentiator) return '';
  return wordSets[0].slice(0, sharedWordCount).join(' ');
}

function removeSharedTitlePrefix(title, prefix) {
  const normalizedTitle = String(title || '').trim();
  const normalizedPrefix = String(prefix || '').trim();
  const prefixWithBoundary = `${normalizedPrefix} `;

  if (!normalizedPrefix || !normalizedTitle.toLowerCase().startsWith(prefixWithBoundary.toLowerCase())) {
    return normalizedTitle;
  }
  return normalizedTitle.slice(prefixWithBoundary.length).trim() || normalizedTitle;
}

function cleanLinkedProductLabel(title, prefix) {
  let label = removeSharedTitlePrefix(title, prefix)
    .replace(/^[\s\-\u2013\u2014:|]+/, '')
    .trim();
  const parenthesizedLabel = label.match(/^\((.+)\)$/);

  if (parenthesizedLabel) label = parenthesizedLabel[1].trim();
  return label || String(title || '').trim();
}

function selectNativeOption(nativeSelect, optionValueId) {
  if (!nativeSelect || !optionValueId) return false;

  const nativeOption = Array.from(nativeSelect.options || []).find(
    (option) => String(option.dataset.optionValueId) === String(optionValueId),
  );

  if (!nativeOption || nativeOption.disabled) return false;

  nativeSelect.value = nativeOption.value;
  nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

class ProductOptionDropdown extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.trigger = this.querySelector('[data-option-trigger]');
    this.panel = this.querySelector('[data-option-panel]');
    this.optionRows = Array.from(this.querySelectorAll('[data-option-row]'));
    this.nativeSelect = this.querySelector('[data-option-select]');

    if (!this.trigger || !this.panel || !this.optionRows.length) return;

    if (this.dataset.source === 'linked-product') this.applyLinkedProductLabels();

    this.abortController = new AbortController();
    const listenerOptions = { signal: this.abortController.signal };

    this.trigger.addEventListener('click', () => this.toggle(), listenerOptions);
    this.addEventListener('focusout', (event) => {
      if (!event.relatedTarget || !this.contains(event.relatedTarget)) this.close();
    }, listenerOptions);
    this.trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event), listenerOptions);
    this.optionRows.forEach((option) => {
      option.addEventListener('click', () => this.selectOption(option), listenerOptions);
      option.addEventListener('keydown', (event) => this.onOptionKeydown(event, option), listenerOptions);
    });
    document.addEventListener('pointerdown', (event) => this.onDocumentPointerDown(event), listenerOptions);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.panel.hidden) {
        event.preventDefault();
        this.close({ restoreFocus: true });
      }
    }, listenerOptions);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  toggle() {
    if (this.panel.hidden) {
      this.open();
    } else {
      this.close();
    }
  }

  open(focusPosition = null) {
    document.querySelectorAll('product-option-dropdown[data-open]').forEach((dropdown) => {
      if (dropdown !== this) dropdown.close();
    });
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.setAttribute('data-open', '');

    if (focusPosition === 'last') {
      const enabledOptions = this.enabledOptions;
      enabledOptions[enabledOptions.length - 1]?.focus();
    } else if (focusPosition) {
      (this.selectedOption && !this.isDisabled(this.selectedOption) ? this.selectedOption : this.enabledOptions[0])?.focus();
    }
  }

  close({ restoreFocus = false } = {}) {
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.removeAttribute('data-open');
    if (restoreFocus) this.trigger.focus();
  }

  onDocumentPointerDown(event) {
    if (!this.panel.hidden && !this.contains(event.target)) this.close();
  }

  onTriggerKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.open('selected');
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.open('last');
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.panel.hidden) this.open('selected');
        else this.close();
        break;
      case 'Escape':
        if (!this.panel.hidden) {
          event.preventDefault();
          this.close({ restoreFocus: true });
        }
        break;
    }
  }

  onOptionKeydown(event, option) {
    const enabledOptions = this.enabledOptions;
    const currentIndex = enabledOptions.indexOf(option);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        enabledOptions[(currentIndex + 1) % enabledOptions.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        enabledOptions[(currentIndex - 1 + enabledOptions.length) % enabledOptions.length]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        enabledOptions[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        enabledOptions[enabledOptions.length - 1]?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectOption(option);
        break;
      case 'Escape':
        event.preventDefault();
        this.close({ restoreFocus: true });
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  selectOption(option) {
    if (this.isDisabled(option)) return;

    if (option.getAttribute('aria-selected') === 'true') {
      this.close({ restoreFocus: true });
      return;
    }

    if (this.nativeSelect && option.dataset.optionValueId) {
      if (!selectNativeOption(this.nativeSelect, option.dataset.optionValueId)) return;
      this.updateSelectionState(option);
      this.close({ restoreFocus: true });
      return;
    }

    if (option.dataset.productUrl) {
      this.updateSelectionState(option);
      this.close({ restoreFocus: true });
      this.navigate(option.dataset.productUrl);
    }
  }

  navigate(url) {
    window.location.assign(url);
  }

  updateSelectionState(selectedOption) {
    this.optionRows.forEach((option) => {
      option.setAttribute('aria-selected', String(option === selectedOption));
    });

    const triggerText = this.querySelector('[data-option-trigger-text]');
    const selectedText = selectedOption.dataset.displayLabel || selectedOption.dataset.value;
    if (triggerText && selectedText) triggerText.textContent = selectedText;
  }

  applyLinkedProductLabels() {
    const sharedPrefix = getSharedTitlePrefix(this.optionRows.map((option) => option.dataset.fullLabel));
    const triggerText = this.querySelector('[data-option-trigger-text]');

    this.optionRows.forEach((option) => {
      const label = option.dataset.labelOverride || cleanLinkedProductLabel(option.dataset.fullLabel, sharedPrefix);
      option.dataset.displayLabel = label;
      const optionText = option.querySelector('[data-option-text]');
      if (optionText) optionText.textContent = label;
      if (triggerText && option.getAttribute('aria-selected') === 'true') triggerText.textContent = label;
    });
  }

  isDisabled(option) {
    return option.disabled || option.dataset.disabled === 'true' || option.getAttribute('aria-disabled') === 'true';
  }

  get enabledOptions() {
    return this.optionRows.filter((option) => !this.isDisabled(option));
  }

  get selectedOption() {
    return this.optionRows.find((option) => option.getAttribute('aria-selected') === 'true');
  }
}

if (!customElements.get('product-option-dropdown')) {
  customElements.define('product-option-dropdown', ProductOptionDropdown);
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    ProductOptionDropdown,
    cleanLinkedProductLabel,
    getSharedTitlePrefix,
    removeSharedTitlePrefix,
    selectNativeOption,
  };
}
